import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, PawPrint } from 'lucide-react';
import { Pet } from '../../types/pet';

interface PetsDropdownProps {
  pets: Pet[];
  onPetClick?: (pet: Pet) => void;
  className?: string;
}

const getSpeciesIcon = (species: string): string => {
  const icons: { [key: string]: string } = {
    dog: '🐕',
    cat: '🐈',
    bird: '🦜',
    rabbit: '🐰',
    horse: '🐴',
    reptile: '🦎',
    fish: '🐠',
  };
  return icons[species.toLowerCase()] || '🐾';
};

const getPetSummaryText = (pets: Pet[]): string => {
  if (pets.length === 0) return 'Pets';
  
  // Count by species
  const speciesCounts: { [key: string]: number } = {};
  pets.forEach(pet => {
    const species = (pet.species || 'pet').toLowerCase();
    speciesCounts[species] = (speciesCounts[species] || 0) + 1;
  });
  
  const species = Object.keys(speciesCounts);
  
  // Single species - show count and species name
  if (species.length === 1) {
    const speciesName = species[0];
    const count = speciesCounts[speciesName];
    const plural = count > 1 ? 's' : '';
    return `${count} ${speciesName}${plural}`;
  }
  
  // Mixed species (2+) - just show total count
  return `${pets.length} pets`;
};

const PetsDropdown: React.FC<PetsDropdownProps> = ({ pets, onPetClick, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUpward: false });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownWidth = 288; // w-72 = 288px
      
      // Estimate dropdown height (each item ~56px + padding)
      const estimatedHeight = Math.min(pets.length * 56 + 8, 320); // max-h-80 = 320px
      
      // Determine if should open upward
      const shouldOpenUpward = spaceBelow < estimatedHeight + 20 && rect.top > estimatedHeight + 20;
      
      // Calculate position
      const top = shouldOpenUpward 
        ? rect.top - 8  // Position above button with small margin
        : rect.bottom + 8;
      
      // Align to right edge of button, but keep within viewport
      const left = Math.max(8, Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 8));
      
      setDropdownPosition({ top, left, openUpward: shouldOpenUpward });
    }
  }, [isOpen, pets.length]);

  if (!pets || pets.length === 0) {
    return (
      <span className="text-sm text-gray-400 italic">
        None
      </span>
    );
  }

  const handlePetClick = (pet: Pet) => {
    console.log('🐕 PetsDropdown: Pet clicked:', pet.name, pet.id);
    setIsOpen(false);
    if (onPetClick) {
      console.log('🐕 Calling onPetClick callback');
      onPetClick(pet);
    } else {
      console.log('⚠️ No onPetClick callback provided!');
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors whitespace-nowrap"
      >
        <span className="whitespace-nowrap">{getPetSummaryText(pets)}</span>
        <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div 
            ref={dropdownRef}
            className="fixed z-50 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-80 overflow-y-auto"
            style={{
              [dropdownPosition.openUpward ? 'bottom' : 'top']: dropdownPosition.openUpward 
                ? `${window.innerHeight - dropdownPosition.top}px`
                : `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            {pets.map((pet) => (
              <button
                key={pet.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePetClick(pet);
                }}
                className="w-full px-3 py-2 hover:bg-blue-50 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded border bg-orange-100 text-orange-700 border-orange-200">
                    {pet.name}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded border ${
                    pet.species?.toLowerCase() === 'dog' 
                      ? 'bg-blue-100 text-blue-700 border-blue-200'
                      : pet.species?.toLowerCase() === 'cat'
                      ? 'bg-purple-100 text-purple-700 border-purple-200'
                      : 'bg-gray-100 text-gray-700 border-gray-200'
                  }`}>
                    {pet.species}
                  </span>
                  {pet.breed && (
                    <span className="text-sm text-gray-500">• {pet.breed}</span>
                  )}
                </div>
              </button>
            ))}
        </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default PetsDropdown;

